// Pure helpers for approximate sharing ("bubbles"): the choices offered when
// starting one, which bubble covers a circle, and the "Approximate · within
// 2 km until 18:00" wording shown for a bubbled member.
import type { Schemas } from "@10twenty/api";
import type { Units } from "../units";

type SharingBubble = Schemas["SharingBubble"];
type MemberBubble = Schemas["MemberBubble"];

/** The radii the server accepts, in metres. */
export const BUBBLE_RADII = [1000, 2000, 5000, 25000] as const;
/** The durations the server accepts, in hours; null lasts until turned off. */
export const BUBBLE_DURATIONS = [1, 3, 6, null] as const;
export const DEFAULT_BUBBLE_RADIUS = 2000;
export const DEFAULT_BUBBLE_DURATION: BubbleDuration = 3;

export type BubbleDuration = (typeof BUBBLE_DURATIONS)[number];

const METRES_PER_MILE = 1609.344;

/** "2 km", or "1.2 mi" in imperial units. */
export function radiusText(metres: number, units: Units = "metric"): string {
  if (units === "metric") return `${Math.round(metres / 100) / 10} km`;
  const miles = metres / METRES_PER_MILE;
  return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
}

/** "1 hour", "3 hours", or "Until I turn it off". */
export function durationText(hours: BubbleDuration): string {
  if (hours === null) return "Until I turn it off";
  return hours === 1 ? "1 hour" : `${hours} hours`;
}

/** "until 18:00" (or "until Mon 18:00"), or null for a bubble without an end. */
export function untilText(until: string | null | undefined, nowMs: number, locale?: string): string | null {
  if (!until) return null;
  const at = Date.parse(until);
  // An end at most 6 h ahead is today or tomorrow, so a weekday is enough.
  const sameDay = new Date(at).toDateString() === new Date(nowMs).toDateString();
  const time = new Date(at).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return sameDay ? `until ${time}` : `until ${new Date(at).toLocaleDateString(locale, { weekday: "short" })} ${time}`;
}

/** "Approximate · within 2 km until 18:00", as a bubbled member is listed. */
export function approximateText(
  bubble: Pick<MemberBubble, "radius_m" | "until">,
  nowMs: number,
  units: Units = "metric",
  locale?: string,
): string {
  const until = untilText(bubble.until, nowMs, locale);
  return `Approximate · within ${radiusText(bubble.radius_m, units)}${until ? ` ${until}` : ""}`;
}

/** Whether a bubble is still on at `nowMs` (the list can outlive an expiry). */
export function isOn(bubble: Pick<SharingBubble, "until">, nowMs: number): boolean {
  return !bubble.until || Date.parse(bubble.until) > nowMs;
}

/** The caller's bubbles that are still on, every-circle ones first, then by start. */
export function activeBubbles(bubbles: readonly SharingBubble[], nowMs: number): SharingBubble[] {
  return bubbles
    .filter((bubble) => isOn(bubble, nowMs))
    .sort(
      (a, b) =>
        Number(a.circle_id !== null) - Number(b.circle_id !== null) ||
        Date.parse(a.started_at) - Date.parse(b.started_at),
    );
}

export interface CircleBubbles {
  /** The bubble started for this circle alone. */
  own: SharingBubble | null;
  /** The bubble started for every circle. */
  all: SharingBubble | null;
  /** What the circle sees: the wider of the two, the newer on a tie (the server's rule), or null. */
  shown: SharingBubble | null;
}

/** The bubbles that cover `circleId`. */
export function bubblesFor(bubbles: readonly SharingBubble[], circleId: string, nowMs: number): CircleBubbles {
  const active = activeBubbles(bubbles, nowMs);
  const own = active.find((bubble) => bubble.circle_id === circleId) ?? null;
  const all = active.find((bubble) => bubble.circle_id === null) ?? null;
  return { own, all, shown: own && all ? wider(own, all) : (own ?? all) };
}

function wider(a: SharingBubble, b: SharingBubble): SharingBubble {
  if (a.radius_m !== b.radius_m) return a.radius_m > b.radius_m ? a : b;
  return Date.parse(b.started_at) > Date.parse(a.started_at) ? b : a;
}

/** "Every circle" or the circle's name (a circle you left drops its bubble, so a miss is a stale list). */
export function bubbleScopeText(
  bubble: Pick<SharingBubble, "circle_id">,
  circles: readonly { id: string; name: string }[],
): string {
  if (bubble.circle_id === null) return "Every circle";
  return circles.find((circle) => circle.id === bubble.circle_id)?.name ?? "Unknown circle";
}

/** The start request body; `circleId` null covers every circle. */
export function bubbleRequest(
  circleId: string | null,
  radiusM: number,
  hours: BubbleDuration,
): Schemas["SharingBubbleCreate"] {
  return { circle_id: circleId, radius_m: radiusM, duration_hours: hours };
}

/** The message for a failed start or end, from the API error code. */
export function bubbleErrorText(code: string | undefined, fallback: string): string {
  if (code === "sos_open") return "You have an open SOS alert. Your circles see your exact location until it ends.";
  if (code === "not_found") return "That circle or bubble no longer exists. Reload the page.";
  if (code === "supervised")
    return "Your supervision asks a guardian before you share an approximate area. Ask them instead.";
  return fallback;
}
